<script setup>
import { onBeforeUnmount, watch } from "vue";

/**
 * Guest mobile navigation drawer (GH-1122, Figma 364-24).
 *
 * Slides in from the right and fills the viewport: the three primary actions
 * as ruled rows, the secondary marketing links, and a red footer bar with the
 * social icons and copyright. Teleported to <body> so it escapes the header's
 * rounded/overflow wrappers, and gates the page scroll while open.
 *
 * The logo and the close control are NOT in here: BrandHeader's own logo and
 * hamburger stay in place above the panel (z-[60]) and change state instead.
 * The invisible spacer at the top mirrors that header row (page px-5/pt-5 +
 * nav px-5/py-5, px-9/py-6 from sm) so the first rule lands just beneath it.
 * Because the header must be on screen to close the drawer, opening scrolls to
 * the top and closing restores the previous scroll position.
 *
 * Links are plain anchors (not Inertia Links) to match BrandHeader: guest
 * pages hop between the brand and legacy stylesheet regimes, so navigation
 * must be a full load.
 */
const props = defineProps({
    open: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(["close", "closed"]);

const primaryLinks = [
    { label: "Sign In", route: "login" },
    { label: "Create Account", route: "register" },
    { label: "Find Inspiration", route: "explore" },
];

const secondaryLinks = [
    { label: "Browser Extension", route: "browser-extensions" },
    { label: "Mobile App", route: "mobile" },
    { label: "About", route: "about-us" },
    { label: "Help", route: "help" },
];

const socialLinks = [
    {
        label: "Gift Hero on Instagram",
        href: "https://www.instagram.com/gift_hero",
        icon: "images/brand/social-1.svg",
    },
    {
        label: "Gift Hero on Facebook",
        href: "https://www.facebook.com/GiftHero",
        icon: "images/brand/social-2.svg",
    },
];

let savedScrollY = 0;

function close() {
    emit("close");
}

function handleKeydown(event) {
    if (event.key === "Escape" && props.open) {
        close();
    }
}

// Lock the document scroll while the drawer is open. The header's hamburger
// doubles as the close control (and keeps focus), so the header has to be in
// view: jump to the top on open and put the reader back on close.
watch(
    () => props.open,
    (isOpen) => {
        if (isOpen) {
            savedScrollY = window.scrollY;
            window.scrollTo({ top: 0, behavior: "instant" });
            document.documentElement.style.overflow = "hidden";
            document.addEventListener("keydown", handleKeydown);
        } else {
            document.documentElement.style.overflow = "";
            document.removeEventListener("keydown", handleKeydown);
            window.scrollTo({ top: savedScrollY, behavior: "instant" });
        }
    },
);

onBeforeUnmount(() => {
    document.documentElement.style.overflow = "";
    document.removeEventListener("keydown", handleKeydown);
});
</script>

<template>
    <Teleport to="body">
        <!-- Drawer -->
        <Transition
            enter-active-class="transition-transform duration-300 ease-out"
            enter-from-class="translate-x-full"
            leave-active-class="transition-transform duration-200 ease-in"
            leave-to-class="translate-x-full"
            @after-leave="emit('closed')"
        >
            <aside
                v-if="open"
                id="brand-mobile-nav"
                class="brand fixed inset-0 z-50 flex flex-col overflow-y-auto bg-brand-navy font-brand text-white lg:hidden"
                role="dialog"
                aria-modal="true"
                aria-label="Navigation"
            >
                <!--
                    Spacer matching the header row (52px hamburger, py-5 /
                    sm:py-6) so the logo and ✕ above sit inside it.
                -->
                <div
                    aria-hidden="true"
                    class="h-[52px] shrink-0 pb-9 pt-10 [box-sizing:content-box] sm:pt-11"
                ></div>

                <!-- Primary actions: ruled 80px rows -->
                <ul class="border-t border-white/10">
                    <li
                        v-for="link in primaryLinks"
                        :key="link.label"
                        class="border-b border-white/10"
                    >
                        <!--
                            Explicit color: legacy `a { color: … }` beats
                            inheritance on pages that load app.scss.
                        -->
                        <a
                            :href="route(link.route)"
                            class="flex h-20 items-center px-10 font-brand-display sm:px-14 text-[29px] font-black italic uppercase leading-none text-white transition hover:text-brand-red"
                        >
                            {{ link.label }}
                        </a>
                    </li>
                </ul>

                <!-- Secondary links, pushed toward the footer bar -->
                <ul class="mb-6 mt-auto px-10 pt-16 sm:px-14">
                    <li v-for="link in secondaryLinks" :key="link.label">
                        <a
                            :href="route(link.route)"
                            class="block text-[17px] leading-[42px] text-white transition hover:text-white/70"
                        >
                            {{ link.label }}
                        </a>
                    </li>
                </ul>

                <!-- Footer bar -->
                <div
                    class="flex shrink-0 items-center justify-between gap-6 bg-brand-red px-10 py-7 sm:px-14"
                >
                    <div class="flex items-center gap-[30px]">
                        <a
                            v-for="social in socialLinks"
                            :key="social.label"
                            :href="social.href"
                            target="_blank"
                            rel="noopener"
                            :aria-label="social.label"
                            class="block size-[33px] transition hover:opacity-75"
                        >
                            <img
                                :src="asset(social.icon)"
                                alt=""
                                class="size-full"
                            />
                        </a>
                    </div>
                    <p
                        class="whitespace-nowrap text-right text-[14px] font-medium leading-[22px] text-white"
                    >
                        © 2026 Gift Hero Inc.<br />All Rights Reserved
                    </p>
                </div>
            </aside>
        </Transition>
    </Teleport>
</template>
